import { ArrowBack as ArrowBackIcon, Settings as SettingsIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Container, DataGrid } from '@/components/common';
import { useGroupDetail } from '@/hooks/groups.hook';
import { useManagersByGroupId } from '@/hooks/groups.hook';
import { formatDate } from '@/util/dateUtils';
import { DateCell, UserTypeCell } from '../components';
import AddManagerModal from './components/AddManagerModal';

/**
 * 그룹 매니저 관리 페이지 컴포넌트
 * 
 * 특정 그룹에 소속된 FT/OPERATOR 매니저를 조회하고 관리하는 페이지입니다.
 * - 그룹에 소속된 FT/OPERATOR 목록 조회
 * - 전체 FT/OPERATOR 목록 조회 및 그룹에 추가
 * - 그룹에서 매니저 제거
 */

const GroupManagersPage = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();

  // 그룹 정보 조회
  const { data: groupDetailData } = useGroupDetail(groupId);
  const groupData = groupDetailData?.groupInfo;

  // 매니저 추가 모달 상태
  const [addManagerModalOpen, setAddManagerModalOpen] = useState(false);

  // 그룹에 소속된 관리자 목록 조회 (FT/OPERATOR만 필터링)
  const { data: groupManagersData, isLoading: isGroupManagersLoading, refetch: refetchGroupManagers } = useManagersByGroupId(groupId);

  // API 응답이 배열 또는 { list/managers } 객체일 수 있음
  const groupManagersRows = useMemo(() => {
    if (!groupManagersData) return [];
    if (Array.isArray(groupManagersData)) return groupManagersData;
    return groupManagersData?.list ?? groupManagersData?.managers ?? [];
  }, [groupManagersData]);

  /** assignedAt (없으면 createdAt) 기준 가장 이른 시점 1명 — role이 OPERATOR / FACILITATOR */
  const { mailingOperator, mailingFacilitator } = useMemo(() => {
    const rows = groupManagersRows;
    const time = (r) => {
      const v = r.assignedAt ?? r.createdAt;
      if (v == null || v === '') return Number.POSITIVE_INFINITY;
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : Number.POSITIVE_INFINITY;
    };
    const pickEarliest = (role) => {
      const filtered = rows.filter((r) => r.role === role);
      if (filtered.length === 0) return null;
      return filtered.reduce((best, cur) => (time(cur) < time(best) ? cur : best));
    };
    return {
      mailingOperator: pickEarliest('OPERATOR'),
      mailingFacilitator: pickEarliest('FACILITATOR'),
    };
  }, [groupManagersRows]);

  // 그룹 소속 매니저 컬럼 정의
  const groupManagersColumns = useMemo(() => [
    { field: 'userId', headerName: '아이디', minWidth: 120, flex: 1 },
    {
      field: 'name',
      headerName: '이름 (이메일)',
      minWidth: 200,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <span>{params.row.name}</span>
          <span style={{ fontSize: '12px', color: '#888' }}>({params.row.email})</span>
        </Box>
      )
    },
    {
      field: 'role',
      headerName: '권한',
      flex: 1,
      minWidth: 100,
      sortable: false,
      renderCell: (params) => (
        <UserTypeCell row={{ userRole: params.row.role }} />
      ),
    },
    {
      field: 'assignedAt',
      headerName: '배정일',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => <DateCell row={params.row} dateField="assignedAt" />,
    },
    {
      field: 'mailingDesignation',
      headerName: '메일링 담당',
      flex: 1,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const id = String(params.row.userId);
        const isOperator =
          mailingOperator != null && id === String(mailingOperator.userId);
        const isFacilitator =
          mailingFacilitator != null && id === String(mailingFacilitator.userId);
        if (isOperator) {
          return (
            <Chip
              label="운영 관련 메일 담당"
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        }
        if (isFacilitator) {
          return (
            <Chip
              label="학습 관련 메일 담당"
              size="small"
              color="warning"
              variant="outlined"
            />
          );
        }
        return null;
      },
    },
  ], [mailingOperator, mailingFacilitator]);


  return (
    <Container title={`${groupData?.groupName || '그룹'} 매니저 관리`}>
      {/* 뒤로가기 버튼 */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/groups')}
          sx={{ mb: 2 }}
        >
          그룹 목록으로
        </Button>
        <Button
          variant="contained"
          startIcon={<SettingsIcon />}
          onClick={() => setAddManagerModalOpen(true)}
        >
          그룹 관리자 설정
        </Button>
      </Stack>

      {/* 그룹 정보 표시 */}
      {groupData && (
        <Box mb={3} p={2} sx={{ backgroundColor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            그룹 정보
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Typography variant="body2">
              <strong>고객사명:</strong> {groupData.groupName || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>그룹명:</strong> {groupData.groupCourse || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>시작일:</strong> {formatDate(groupData.groupStartDate) || '-'}
            </Typography>
            <Typography variant="body2">
              <strong>종료일:</strong> {formatDate(groupData.groupDueDate) || '-'}
            </Typography>
          </Stack>
        </Box>
      )}

      {/* 그룹 소속 매니저 목록 */}
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          그룹 소속 매니저 목록
        </Typography>

        {/* 데이터 그리드 */}
        <DataGrid
          rows={groupManagersRows}
          columns={groupManagersColumns}
          loading={isGroupManagersLoading}
          getRowId={(row) => row.userId}
          pagination={false}
          height="auto"
          sx={{
            '& .MuiDataGrid-row': {
              cursor: 'default',
            },
            '& .MuiDataGrid-row:hover': {
              cursor: 'default',
            },
          }}
        />
      </Box>

      {/* 매니저 관리 모달 */}
      <AddManagerModal
        open={addManagerModalOpen}
        onClose={() => setAddManagerModalOpen(false)}
        groupId={parseInt(groupId)}
        groupManagerIds={groupManagersRows.map(manager => manager.userId)}
        onSuccess={() => {
          refetchGroupManagers();
        }}
      />
    </Container>
  );
};

export default GroupManagersPage;
